<template>
  <article class="document-page">
    <header class="document-header">
      <div class="title-row">
        <h1>{{ title }}</h1>
        <span v-if="introducedIn" class="release-badge">Introduced in v{{ introducedIn }}</span>
      </div>
      <SafeHtml v-if="description" :content="description" />
    </header>

    <section v-if="request" class="document-section">
      <h2>Request</h2>
      <div class="endpoint-panel">
        <span class="method-pill" :class="request.toLowerCase()">{{ request.toUpperCase() }}</span>
        <code>{{ endpoint }}</code>
        <CopyButton :value="endpoint" label="endpoint" />
      </div>
    </section>

    <section v-if="request" class="document-section">
      <h2>Parameters</h2>
      <template v-if="parameters && parameters.length">
        <div class="table-wrap parameter-table">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Required</th>
                <th>Type</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="parameter in parameters" :key="parameter.name">
                <tr>
                  <td><code>{{ parameter.name }}</code></td>
                  <td><span class="required-pill" :class="{ required: isRequired(parameter.required) }">{{ parameter.required }}</span></td>
                  <td>{{ parameter.type }}</td>
                  <td>{{ parameter.description }}</td>
                </tr>
                <tr v-for="child in parameter.childparameters || []" :key="`${parameter.name}-${child.name}`" class="child-parameter">
                  <td><code>{{ parameter.name }}.{{ child.name }}</code></td>
                  <td><span class="required-pill" :class="{ required: isRequired(child.required) }">{{ child.required }}</span></td>
                  <td>{{ child.type }}</td>
                  <td>{{ child.description }}</td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
        <div class="parameter-cards">
          <div v-for="parameter in flattenedParameters" :key="parameter.name" class="parameter-card">
            <div class="parameter-heading">
              <code>{{ parameter.name }}</code>
              <span class="required-pill" :class="{ required: isRequired(parameter.required) }">{{ parameter.required }}</span>
            </div>
            <p class="parameter-type">{{ parameter.type }}</p>
            <p v-if="parameter.description">{{ parameter.description }}</p>
          </div>
        </div>
      </template>
      <p v-else class="muted-text">No parameters.</p>
    </section>

    <section v-if="request" class="document-section">
      <div class="section-heading">
        <div>
          <h2>Example request</h2>
          <p class="sample-caption">{{ activeExampleMeta.caption }}</p>
        </div>
        <CopyButton :value="activeExample" :label="`${activeExampleMeta.label} example`" />
      </div>
      <div class="example-tabs" role="tablist" aria-label="Example language">
        <button
          v-for="example in examples"
          :key="example.id"
          class="example-tab"
          :class="{ active: selectedExample === example.id }"
          type="button"
          role="tab"
          :aria-selected="selectedExample === example.id"
          @click="selectExample(example.id)"
        >
          {{ example.label }}
        </button>
      </div>
      <pre class="code-panel"><code>{{ activeExample }}</code></pre>
    </section>

    <section v-if="request" class="document-section">
      <h2>Response</h2>
      <p class="response-status"><span class="status-dot"></span>{{ response }}</p>
      <div v-if="returns" class="section-heading code-heading">
        <h3>Response body</h3>
        <CopyButton :value="formattedReturns" label="response body" />
      </div>
      <pre v-if="returns" class="code-panel"><code>{{ formattedReturns }}</code></pre>
    </section>

    <section v-if="sectionNumber === '2'" class="document-section">
      <h2>HTTP status codes</h2>
      <div class="table-wrap status-table">
        <table>
          <thead>
            <tr>
              <th>Status</th>
              <th>Definition</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in errors" :key="item.code">
              <td><strong>{{ item.code }}</strong></td>
              <td>{{ item.definition }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  </article>
</template>

<script>
import { computed, ref } from 'vue';
import CopyButton from './CopyButton.vue';
import SafeHtml from './SafeHtml.vue';

const EXAMPLE_LANGUAGE_STORAGE_KEY = 'apiDocsExampleLanguage';
const exampleLanguageOptions = ['javascript', 'python'];
const getStoredExampleLanguage = () => {
  const storedLanguage = window.localStorage.getItem(EXAMPLE_LANGUAGE_STORAGE_KEY);
  return exampleLanguageOptions.includes(storedLanguage) ? storedLanguage : 'javascript';
};

export default {
  name: 'HTTPRequest',
  components: { CopyButton, SafeHtml },
  props: {
    sectionNumber: { type: String, required: true },
    title: { type: String, default: '' },
    description: { type: String, default: '' },
    request: { type: String, default: '' },
    endpoint: { type: String, default: '' },
    parameters: { type: Array, default: null },
    body: { type: [String, Object, Array], default: '' },
    returns: { type: [String, Object, Array], default: '' },
    introducedIn: { type: String, default: '' }
  },
  setup(props) {
    const formatPayload = (payload) => typeof payload === 'string' ? payload : JSON.stringify(payload, null, 2);
    const makePlaceholder = (name) => `YOUR_${name.replace(/([a-z])([A-Z])/g, '$1_$2').toUpperCase()}`;
    const selectedExample = ref(getStoredExampleLanguage());
    const selectExample = (language) => {
      selectedExample.value = language;
      window.localStorage.setItem(EXAMPLE_LANGUAGE_STORAGE_KEY, language);
    };
    const response = computed(() => props.request === 'ping' ? 'PONG' : '200 OK');
    const formattedBody = computed(() => formatPayload(props.body));
    const formattedReturns = computed(() => formatPayload(props.returns));
    const endpointParameterNames = computed(() => Array.from((props.endpoint || '').matchAll(/:([A-Za-z0-9_]+)/g), (match) => match[1]));
    const javascriptExample = computed(() => {
      const variables = endpointParameterNames.value.map((name) => `const ${name} = '${makePlaceholder(name)}';`);
      const endpoint = (props.endpoint || '').replace(/:([A-Za-z0-9_]+)/g, '${$1}');
      const options = [`  method: '${props.request.toUpperCase()}'`];

      if (props.body) {
        const indentedBody = formattedBody.value.split('\n').map((line) => `    ${line}`).join('\n');
        options[options.length - 1] += ',';
        options.push("  headers: { 'Content-Type': 'application/json' },");
        options.push(`  body: JSON.stringify(\n${indentedBody}\n  )`);
      }

      const parseResponse = props.returns
        ? [
          '',
          'const data = await response.json();',
          'console.log(data);'
        ]
        : [
          '',
          'console.log(response.status);'
        ];

      return [
        "const baseUrl = 'http://[your-api-host]:[your-api-port]';",
        ...(variables.length ? ['', ...variables] : []),
        '',
        `const response = await fetch(\`${'${baseUrl}'}${endpoint}\`, {`,
        ...options,
        '});',
        '',
        "if (!response.ok) throw new Error(`Request failed: ${response.status}`);",
        ...parseResponse
      ].join('\n');
    });
    const pythonExample = computed(() => {
      const variables = endpointParameterNames.value.map((name) => `${name} = '${makePlaceholder(name)}'`);
      const endpoint = (props.endpoint || '').replace(/:([A-Za-z0-9_]+)/g, '{$1}');
      const imports = ['import requests'];
      const lines = [
        ...imports,
        ...(props.body ? ['import json'] : []),
        '',
        "base_url = 'http://[your-api-host]:[your-api-port]'",
        ...(variables.length ? ['', ...variables] : [])
      ];

      if (props.body) {
        lines.push(
          '',
          "payload = json.loads(r'''",
          formattedBody.value,
          "''')",
          '',
          `response = requests.${props.request.toLowerCase()}(f'{base_url}${endpoint}', json=payload)`
        );
      } else {
        lines.push('', `response = requests.${props.request.toLowerCase()}(f'{base_url}${endpoint}')`);
      }

      lines.push('', 'response.raise_for_status()');
      if (props.returns) {
        lines.push('', 'data = response.json()', 'print(data)');
      } else {
        lines.push('', 'print(response.status_code)');
      }

      return lines.join('\n');
    });
    const examples = computed(() => [
      {
        id: 'javascript',
        label: 'JavaScript',
        caption: 'JavaScript using fetch (browser or Node.js 18+)',
        code: javascriptExample.value
      },
      {
        id: 'python',
        label: 'Python',
        caption: 'Python using requests',
        code: pythonExample.value
      }
    ]);
    const activeExampleMeta = computed(() => examples.value.find((example) => example.id === selectedExample.value) || examples.value[0]);
    const activeExample = computed(() => activeExampleMeta.value.code);
    const flattenedParameters = computed(() => (props.parameters || []).flatMap((parameter) => [
      parameter,
      ...(parameter.childparameters || []).map((child) => ({
        ...child,
        name: `${parameter.name}.${child.name}`
      }))
    ]));
    const isRequired = (value) => String(value).toLowerCase() === 'true' || String(value).toLowerCase() === 'yes';
    const errors = [
      { code: 200, definition: 'OK' },
      { code: 400, definition: 'Bad Request: your request failed, often due to a missing parameter.' },
      { code: 401, definition: 'Unauthorized: the request is not authorized.' },
      { code: 403, definition: 'Forbidden: the request does not have permission to perform this operation.' },
      { code: 404, definition: 'Not Found: the requested resource does not exist.' },
      { code: 406, definition: 'Not Acceptable: object already exists.' },
      { code: 440, definition: 'Signature Invalid: license signature validation failed.' },
      { code: 441, definition: 'License Invalid: the supplied license is not valid.' },
      { code: 442, definition: 'Corrupt License: the supplied license could not be read.' },
      { code: 443, definition: 'Verification Error: license verification failed.' },
      { code: 444, definition: 'License has expired: contact the IOSEA team to update your license.' },
      { code: 445, definition: 'Configuration or license validation error: the Co-pilot cache is unavailable or clock tampering was detected.' },
      { code: 447, definition: 'Locator Limit Reached: the license permits no additional locators.' },
      { code: 448, definition: 'Service Limited: this service is unavailable under the current license.' },
      { code: 500, definition: 'Internal Server Error: the server encountered an issue.' },
      { code: 503, definition: 'Service Unavailable: a required service is temporarily unavailable.' }
    ];

    return {
      activeExample,
      activeExampleMeta,
      errors,
      examples,
      flattenedParameters,
      formattedBody,
      formattedReturns,
      isRequired,
      response,
      selectExample,
      selectedExample
    };
  }
};
</script>
